import { getCurrentFocusKey } from "@noriginmedia/norigin-spatial-navigation";
import type { ChannelGroupId } from "@sunrise/backend-types-core";
import {
  actionDialogClose,
  actionDialogOpen,
  dialogAtom,
} from "@sunrise/dialogs";
import { currentLanguageAtom, getLocalizedValue } from "@sunrise/i18n";
import { useTranslator } from "@sunrise/translator";
import { type Nullable } from "@sunrise/utils";
import {
  currentChannelGroupForDisplayingAtom,
  currentChannelGroupIdAtom,
  selectableChannelGroupsAtom,
} from "@sunrise/yallo-channel-group";
import { useAtomValue, useStore } from "jotai";
import { useCallback } from "react";

/**
 * A hook depending on the dialogs module that will return the currently selected channel group's id and name + a function to trigger a dialog to change the channel group.
 *
 * @returns
 */
export function useChannelGroupSelector(): {
  current: Nullable<{
    name: string;
    id: ChannelGroupId;
  }>;
  askToChange: () => Promise<void>;
} {
  const current = useAtomValue(currentChannelGroupForDisplayingAtom);
  const store = useStore();
  const t = useTranslator();

  const askToChange = useCallback(async (): Promise<void> => {
    const options = await store.get(selectableChannelGroupsAtom);
    const language = store.get(currentLanguageAtom);

    store.set(
      dialogAtom,
      actionDialogOpen({
        id: "channel-group-selector",
        type: "list",
        title: t("channelListExtendedTitle"),
        buttonTextAlign: "left",
        onButtonClick: (value) => {
          store.set(currentChannelGroupIdAtom, value as ChannelGroupId);
          store.set(dialogAtom, actionDialogClose());
        },
        sections: [
          {
            options: options.map((group) => ({
              label: getLocalizedValue(group.display_name, language) ?? "",
              value: group.id,
            })),
          },
        ],
        activeOptions: current ? [current.id] : [],
        lastFocusKey: getCurrentFocusKey(),
      }),
    );
  }, [store, current, t]);

  return {
    current,
    askToChange,
  };
}
